import React, { useState, useContext } from 'react';
import { Share2, Copy, Check, X, Clock, LinkIcon } from 'lucide-react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';

const ShareProcessButton = ({ processId, processName }) => {
  const API = useContext(ApiContext);
  const [showModal, setShowModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);

  const generateShareLink = async () => {
    setLoading(true);
    try {
      const response = await API.post(`/api/process-bizdev/${processId}/share/generate`, {
        expiresInDays
      });

      setShareData(response.data);
      toast.success('Lien de partage généré !');
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Erreur lors de la génération du lien');
    } finally {
      setLoading(false);
    }
  };

  const disableSharing = async () => {
    try {
      await API.post(`/api/process-bizdev/${processId}/share/disable`);
      setShareData(null);
      toast.success('Partage désactivé');
      setShowModal(false);
    } catch (error) {
      console.error('Error disabling share:', error);
      toast.error('Erreur lors de la désactivation du partage');
    }
  };

  const copyToClipboard = () => {
    if (shareData?.shareUrl) {
      navigator.clipboard.writeText(shareData.shareUrl);
      setCopied(true);
      toast.success('Lien copié dans le presse-papier !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openModal = async () => {
    setShowModal(true);
    // Vérifier si un lien existe déjà
    try {
      const response = await API.get(`/api/process-bizdev/${processId}`);
      if (response.data.shareEnabled && response.data.shareToken) {
        setShareData({
          shareToken: response.data.shareToken,
          shareUrl: `${window.location.origin}/shared/process-bizdev/${response.data.shareToken}`,
          expiresAt: response.data.shareExpiresAt
        });
      }
    } catch (error) {
      console.error('Error checking existing share:', error);
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
      >
        <Share2 size={18} />
        Partager
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Share2 className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Partager le processus</h2>
                  <p className="text-sm text-gray-500">{processName}</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {!shareData ? (
                <>
                  {/* Informations */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">
                      À propos du partage
                    </h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Les clients pourront voir l'état du processus en temps réel</li>
                      <li>• Ils verront le nombre de prospects par étape</li>
                      <li>• Les informations sensibles ne seront pas visibles</li>
                      <li>• Le lien sera accessible sans authentification</li>
                    </ul>
                  </div>

                  {/* Durée de validité */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée de validité du lien
                    </label>
                    <select
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={7}>7 jours</option>
                      <option value={14}>14 jours</option>
                      <option value={30}>30 jours</option>
                      <option value={60}>60 jours</option>
                      <option value={90}>90 jours</option>
                    </select>
                  </div>

                  {/* Bouton de génération */}
                  <button
                    onClick={generateShareLink}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                        Génération...
                      </>
                    ) : (
                      <>
                        <LinkIcon size={18} />
                        Générer le lien de partage
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* Lien généré */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Lien de partage
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={shareData.shareUrl}
                        readOnly
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 text-sm"
                      />
                      <button
                        onClick={copyToClipboard}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
                      >
                        {copied ? (
                          <>
                            <Check size={16} className="text-green-600" />
                            <span className="text-sm text-green-600">Copié</span>
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            <span className="text-sm">Copier</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Informations d'expiration */}
                  {shareData.expiresAt && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                      <Clock size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-900">
                          Ce lien expirera le{' '}
                          {new Date(shareData.expiresAt).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <p className="text-xs text-yellow-700 mt-1">
                          Après cette date, le lien ne sera plus accessible
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={generateShareLink}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Régénérer le lien
                    </button>
                    <button
                      onClick={disableSharing}
                      className="flex-1 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                    >
                      Désactiver le partage
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareProcessButton;
